// NPM imports
import React, { useState, useRef, useEffect } from "react";

import { NumberInput } from "../../revvComponents";

// Internal Imports
import { GRID } from "../../constants";

// Destructuring
const { PRICING_METHODS } = GRID;

// Const Declarations
const MAX_QTY = 1000000000000000000000;
let oldBasePrice = "";
let oldPriceType = "";

// Component
export default function PricingPreview(props) {
  // Local State Declarations
  const INITIAL_STATE = {
    price: 0,
    unitPrice: 0,
    units: "",
    view: "units",
  };
  const [state, setState] = useState(INITIAL_STATE);

  // cDM / cDU to set local variables
  useEffect(() => {
    oldPriceType = props.data.type;
    oldBasePrice = props.data.basePrice;
  }, [props.data]);

  // Reset Local State to Inital on unmount
  useEffect(
    () => () => {
      if (!props.readOnly)
        props.onChange({
          price: 0,
          unitPrice: 0,
          units: "",
          view: "units",
        });
    },
    [props],
  );

  // Declarations
  const previewRef = useRef(null);

  // Destructuring
  const { type, range } = props.data;

  // Local Function
  // function _toggleView(name) {
  //   if (state.view !== name) {
  //     const cloneState = { ...state };
  //     cloneState.view = name;
  //     return setState(cloneState);
  //   }
  //   return null;
  // }

  function _updateState(cloneState) {
    if (!props.readOnly) props.onChange(cloneState);
    setState(cloneState);
  }

  function _onUnitsChange(value) {
    let sum = 0;
    let price = null;
    let qty = value;
    const units = value;
    const cloneRange = range && range.map(item => ({ ...item }));
    const cloneState = { ...state };
    const basePrice = props.data.basePrice
      ? parseFloat(props.data.basePrice)
      : 0;
    // Set a max value for the last entry in a range for the below match calc
    if (
      cloneRange &&
      cloneRange[cloneRange.length - 1] &&
      !cloneRange[cloneRange.length - 1].max
    )
      cloneRange[cloneRange.length - 1].max = MAX_QTY;

    // update the units value
    cloneState.units = units;

    // Set values based on price type
    switch (type) {
      case PRICING_METHODS.FLAT_PRICE:
        cloneState.price = units * basePrice;
        cloneState.unitPrice = basePrice;
        return _updateState(cloneState);
      case PRICING_METHODS.VOLUME_PRICING:
        [price] = cloneRange.filter(
          num => num.min <= units && num.max >= units,
        );
        if (price) {
          cloneState.price = units * price.value + basePrice;
          cloneState.unitPrice = ((units * price.value) / (units || 1)).toFixed(
            2,
          ); // units can be empty, so infinity might happen
        }
        return _updateState(cloneState);
      case PRICING_METHODS.STEP_TIER_PRICING:
        [price] = cloneRange.filter(
          num => num.min <= units && num.max >= units,
        );
        if (price) {
          cloneState.price = parseFloat(price.value || 0) + basePrice;
          cloneState.unitPrice = price.value || 0;
        }
        return _updateState(cloneState);
      case PRICING_METHODS.TIER_PRICING:
        cloneRange.every(item => {
          const correction = item.min === 0 ? 0 : 1; // since the range starts from 0 for the first range
          const maxRange = item.max ? item.max : MAX_QTY;
          const items = maxRange - item.min + correction;
          if (qty >= items) {
            sum += items * item.value;
            qty -= items;
            return true;
          }
          sum += qty * item.value;
          return false;
        });
        cloneState.price = sum + basePrice;
        cloneState.unitPrice = (sum / (units || 1)).toFixed(2); // units can be empty, so infinity might happen
        return _updateState(cloneState);
      default:
        break;
    }
    // update the units value for Flat & Bundle Type
    cloneState.units = units + basePrice;
    return _updateState(cloneState);
  }

  function _decidePreview() {
    const { currencySymbol = "$" } = props;
    const basePrice = props.data.basePrice
      ? parseFloat(props.data.basePrice).toFixed(2)
      : 0;
    const price = parseFloat(state.price).toFixed(2);
    switch (props.data.type) {
      case PRICING_METHODS.FLAT_PRICE:
        return (
          <span>
            units, then price per unit is,
            <b>
              {currencySymbol}
              {state.unitPrice}
            </b>{" "}
            and total value is{" "}
            <b>
              {currencySymbol}
              {price}
            </b>
          </span>
        );
      case PRICING_METHODS.TIER_PRICING:
        return (
          <span>
            units, then average price per unit is{" "}
            <b>
              {currencySymbol}
              {state.unitPrice}
            </b>
            , base price is{" "}
            <b>
              {currencySymbol}
              {basePrice}
            </b>{" "}
            and total value is{" "}
            <b>
              {currencySymbol}
              {price}
            </b>
          </span>
        );
      case PRICING_METHODS.VOLUME_PRICING:
        return (
          <span>
            units, then price per unit is{" "}
            <b>
              {currencySymbol}
              {state.unitPrice}
            </b>
            , base price is{" "}
            <b>
              {currencySymbol}
              {basePrice}
            </b>{" "}
            and total value is{" "}
            <b>
              {currencySymbol}
              {price}
            </b>
          </span>
        );
      case PRICING_METHODS.STEP_TIER_PRICING:
        return (
          <span>
            units, then price for the corresponding range is{" "}
            <b>
              {currencySymbol}
              {state.unitPrice}
            </b>
            , base price is{" "}
            <b>
              {currencySymbol}
              {basePrice}
            </b>{" "}
            and total value is{" "}
            <b>
              {currencySymbol}
              {price}
            </b>
          </span>
        );
      default:
        return (
          <span>
            Units, then price per range will be{" "}
            <b>
              {currencySymbol}
              {price}
            </b>
          </span>
        );
    }
  }

  // getDerivedStateFromProps logic
  if (
    (oldPriceType !== props.data.type ||
      oldBasePrice !== props.data.basePrice) &&
    previewRef.current
  ) {
    _onUnitsChange(previewRef.current.value);
    oldBasePrice = props.data.basePrice;
    oldPriceType = props.data.type;
  }

  // Render Section
  return (
    <section className="pricing-preview-wrapper">
      <div className="dealtable-pricing-preview">
        <div className="pricing-preview-title">Price Preview</div>
        <div className="pricing-preview-toggle">
          {/* type === "Volume Pricing" && (
          <Fragment>
            <span
              className="pricing-preview-toggleleft "
              onClick={() => {
                _toggleView("units");
              }}>
              units
            </span>
            <span
              className="pricing-preview-toggleright"
              onClick={() => {
                _toggleView("price");
              }}>
              price
            </span>
          </Fragment>
        ) */}
        </div>
        <div className="pricing-preview-total">
          If Customer buys
          <span>
            <NumberInput
              className="form-control input-units"
              data-test="if-Customer-buys-no-of-unit-input"
              placeholder="Enter no.of units"
              value={state.units}
              onChange={value => _onUnitsChange(value)}
              inputRef={previewRef}
            />
          </span>
          {_decidePreview()}
        </div>

        {/* {state.view === "price" && (
          <div className="pricing-preview-total">
            If Customer pays $
            <span>
              <Input
                className="form-control input-units"
                placeholder="Enter Price"
              />
            </span>
            , then number of units sold will be <b>0</b>
          </div>
        )} */}
      </div>
    </section>
  );
}
